<template>
  <div>
    <!-- Controles -->
    <div class="d-flex align-center flex-wrap mb-3" style="gap: 12px;">
      <v-select
        v-model="year"
        :items="yearOptions"
        label="Año"
        dense outlined hide-details
        style="max-width: 130px;"
        @change="fetchData"
      />
      <v-spacer />
      <span v-if="snapshotDate" class="snapshot-note">
        MRR al {{ snapshotDate | date }}
      </span>
    </div>

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="!months.length" class="text-center grey--text py-8">
      <v-icon size="40" color="grey lighten-2">mdi-chart-timeline-variant</v-icon>
      <div class="mt-2">No hay datos de MRR para este año</div>
    </div>

    <div v-else class="table-scroll">
      <table class="mrr-table">
        <thead>
          <tr>
            <th class="row-label"></th>
            <th
              v-for="m in months"
              :key="m.month"
              :class="['month-head', `month-head--${m.status}`]"
            >
              {{ monthName(m.month) }}
              <div class="month-status">{{ statusLabel(m.status) }}</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Flujo -->
          <tr class="section-row">
            <td class="row-label">MRR New</td>
            <td v-for="m in months" :key="m.month" :class="cellClass(m)">
              {{ usd(m.newMrr) }}
            </td>
          </tr>
          <tr>
            <td class="row-label">MRR New acumulado</td>
            <td v-for="m in months" :key="m.month" :class="cellClass(m)">
              {{ usd(m.newMrrAccumulated) }}
            </td>
          </tr>

          <tr class="spacer-row"><td :colspan="months.length + 1"></td></tr>

          <!-- De dónde viene -->
          <tr class="section-title-row">
            <td class="row-label" :colspan="months.length + 1">De dónde viene</td>
          </tr>
          <tr v-for="k in breakdownKinds" :key="k.key">
            <td class="row-label row-label--sub" :title="k.hint">{{ k.label }}</td>
            <td
              v-for="m in months"
              :key="m.month"
              :class="[cellClass(m), { clickable: canSeeContracts }]"
              @click="openMovements(m, k.key)"
            >
              {{ usd(m[k.field]) }}
            </td>
          </tr>
          <tr>
            <td class="row-label row-label--sub" :title="unattributedRow.hint">
              {{ unattributedRow.label }}
              <v-icon x-small color="#8593A3" class="ml-1">mdi-help-circle-outline</v-icon>
            </td>
            <td
              v-for="m in months"
              :key="m.month"
              :class="[cellClass(m), { clickable: canSeeContracts && m[unattributedRow.field] }]"
              @click="m[unattributedRow.field] && openMovements(m, unattributedRow.key)"
            >
              {{ usd(m[unattributedRow.field]) }}
            </td>
          </tr>
          <tr class="net-row">
            <td class="row-label">Variación neta</td>
            <td v-for="m in months" :key="m.month" :class="cellClass(m)">
              {{ usd(m.netNewMrr) }}
            </td>
          </tr>
          <!-- La lista de trabajo para quien carga facturación: contratos que
               deberían tener datos este mes y no tienen. Mide un estado, no un
               cambio — un contrato vacío mes tras mes nunca "cambia", y con la
               regla anterior no aparecía nunca. -->
          <tr v-if="hasMissingData">
            <td
              class="row-label row-label--sub gap-label"
              title="Contratos vigentes desde antes del mes, en líneas que guardan meses cerrados, sin nada cargado para ese mes. Se corrige cargando la facturación. No incluye pausados, internos ni líneas que se calculan solo en vivo (B2B2C Pago Directo, B2C)."
            >
              Contratos activos sin MRR cargado
              <v-icon x-small color="#B26500" class="ml-1">mdi-help-circle-outline</v-icon>
            </td>
            <td
              v-for="m in months"
              :key="m.month"
              :class="[cellClass(m), 'gap-cell', { clickable: canSeeContracts && m.missingDataCount > 0 }]"
              @click="m.missingDataCount > 0 && openMovements(m, 'missingData')"
            >
              <span v-if="m.missingDataCount > 0">
                {{ m.missingDataCount }} {{ m.missingDataCount === 1 ? 'contrato' : 'contratos' }}
              </span>
              <span v-else>—</span>
            </td>
          </tr>

          <tr class="spacer-row"><td :colspan="months.length + 1"></td></tr>

          <tr class="section-title-row">
            <td class="row-label" :colspan="months.length + 1">Total y metas</td>
          </tr>

          <!-- Stock -->
          <tr class="section-row">
            <td
              class="row-label"
              title="Solo las líneas de negocio reconocidas por país, igual que MRR Resumen. Una diferencia: en los meses cerrados incluye B2C y B2B2C Pago Directo tal como estaban en el último snapshot del mes, que MRR Resumen no muestra porque esas líneas no guardan meses cerrados. El mes en curso se lee de la foto diaria, así que puede diferir unos dólares de MRR Resumen, que se calcula en vivo."
            >
              MRR Total
              <v-icon x-small class="ml-1">mdi-help-circle-outline</v-icon>
            </td>
            <td v-for="m in months" :key="m.month" :class="cellClass(m)">
              {{ usd(m.totalMrr) }}
            </td>
          </tr>
          <tr>
            <td class="row-label">ARR Total</td>
            <td v-for="m in months" :key="m.month" :class="cellClass(m)">
              {{ usd(m.arr) }}
            </td>
          </tr>
          <tr>
            <td class="row-label">
              Crecimiento
              <v-tooltip bottom max-width="340">
                <template v-slot:activator="{ on }">
                  <v-icon x-small class="ml-1" v-on="on">mdi-help-circle-outline</v-icon>
                </template>
                <span>
                  Variación del MRR Total contra el mes anterior. Incluye el
                  efecto de los contratos activos sin MRR cargado, así que un
                  mes con carga incompleta puede mostrar una caída que no es de
                  negocio — la fila "Contratos activos sin MRR cargado" avisa
                  cuándo mirar con cuidado.
                </span>
              </v-tooltip>
            </td>
            <td
              v-for="(m, i) in months"
              :key="m.month"
              :class="[cellClass(m), growthClass(i)]"
            >
              {{ growthLabel(i) }}
            </td>
          </tr>
          <tr>
            <td class="row-label">Meta del trimestre</td>
            <td v-for="m in months" :key="m.month" :class="cellClass(m)">
              {{ m.goalUsd !== null ? usd(m.goalUsd) : '—' }}
            </td>
          </tr>
          <tr>
            <td class="row-label">Cumplimiento</td>
            <td v-for="m in months" :key="m.month" :class="cellClass(m)">
              <!-- Vacío y no 0%: sin meta cargada no es incumplimiento. -->
              <span v-if="m.goalAttainmentPct !== null">{{ m.goalAttainmentPct }}%</span>
              <span v-else class="grey--text">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Detalle de una categoría -->
    <v-dialog v-model="detailDialog" max-width="960" @keydown.esc="detailDialog = false">
      <v-card>
        <v-card-title class="text-h6">{{ detailTitle }}</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-progress-linear v-if="loadingDetail" indeterminate color="primary" />
          <!-- El criterio de cada fila, escrito: el modal se abre para
               responder "¿de dónde sale este número?". -->
          <p v-if="!loadingDetail && detailHint" class="detail-hint">{{ detailHint }}</p>

          <!-- Faltantes: un estado, así que no hay aporte que mostrar. Lo que
               sirve para cargarlos es desde cuándo están vigentes. -->
          <v-simple-table v-if="!loadingDetail && detailRows.length && detailKind === 'missingData'" dense class="detail-table">
            <thead>
              <tr>
                <th>Contrato</th><th>Empresa</th><th>Producto</th><th>Línea</th>
                <th>Vigente desde</th>
                <th class="text-right">Mes anterior</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in detailRows" :key="`${r.contrato}-${i}`">
                <td>{{ r.contrato }}</td>
                <td>{{ r.empresa || '—' }}</td>
                <td>{{ r.producto || '—' }}</td>
                <td>{{ r.lineaNegocio || '—' }}</td>
                <td>{{ r.startDate || '—' }}</td>
                <td class="text-right">{{ usd(r.previousMrr) }}</td>
                <td class="text-right">
                  <v-btn v-if="r.contratoId && can('mrr-new:create-billing')" small text color="primary" @click="openLoad(r)">Cargar</v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-simple-table v-else-if="!loadingDetail && detailRows.length" dense class="detail-table">
            <thead>
              <tr>
                <th>Contrato</th><th>Empresa</th><th>Producto</th><th>Línea</th><th>Canal</th>
                <th class="text-right">Mes anterior</th>
                <th class="text-right">Este mes</th>
                <th class="text-right">Aporte</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in detailRows" :key="`${r.contrato}-${i}`">
                <td>{{ r.contrato }}</td>
                <td>{{ r.empresa || '—' }}</td>
                <td>{{ r.producto || '—' }}</td>
                <td>{{ r.lineaNegocio || '—' }}</td>
                <td>{{ r.canal || 'Directo' }}</td>
                <td class="text-right">{{ usd(r.previousMrr) }}</td>
                <td class="text-right">{{ usd(r.currentMrr) }}</td>
                <td class="text-right font-weight-bold">{{ usd(r.deltaUsd) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div v-if="!loadingDetail && !detailRows.length" class="grey--text py-4">
            Sin contratos en esta categoría.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Carga el mes que falta sin pasar por Finanzas > Registro de MRR: crea
         el mismo registro de billing que el formulario de allá. -->
    <v-dialog v-model="loadDialog" max-width="480" @keydown.esc="loadDialog = false">
      <v-card class="pa-4">
        <v-card-title class="text-uppercase">Cargar MRR</v-card-title>
        <v-card-subtitle class="pb-0">
          {{ loadItem.contrato }} — {{ loadItem.empresa }} — {{ monthName(loadItem.month) }} {{ loadItem.year }}
        </v-card-subtitle>
        <hr class="my-3" />
        <v-card-text>
          <v-form ref="loadForm" v-model="loadFormValid" @submit.prevent="saveLoad">
            <v-row>
              <v-col cols="6">
                <v-text-field
                  label="MRR Local"
                  v-model.number="loadItem.mrrLocal"
                  type="number"
                  autofocus
                  outlined dense hide-details="auto"
                  :rules="[v => (v !== null && v !== undefined && v !== '') || 'Requerido']"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  label="Moneda"
                  v-model="loadItem.moneda"
                  :items="currencies"
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="Notas"
                  v-model="loadItem.notas"
                  outlined dense hide-details="auto"
                />
              </v-col>
            </v-row>
          </v-form>
          <v-alert v-if="loadError" type="error" dense class="mt-3">
            Ocurrió un error al guardar, intenta más tarde.
          </v-alert>
        </v-card-text>
        <hr class="my-2" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded :disabled="loadSaving" @click="loadDialog = false">Cerrar</v-btn>
          <v-btn color="primary" rounded :disabled="!loadFormValid" :loading="loadSaving" @click="saveLoad">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import NewMrrReportService from '@/services/new-mrr-report.service';
import BillingService from '@/services/billing.service';
import { utilsMixin } from '@/mixins/utilsMixin';
import { can } from '@/utils/permissions';

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const BREAKDOWN = [
  { key: 'new', field: 'newMrr', label: 'Altas' },
  { key: 'reactivation', field: 'reactivationMrr', label: 'Reactivaciones' },
  { key: 'expansion', field: 'expansionMrr', label: 'Expansión' },
  { key: 'contraction', field: 'contractionMrr', label: 'Contracción' },
  {
    key: 'churn',
    field: 'churnMrr',
    label: 'Churn',
    hint: 'Clientes perdidos. No incluye renovaciones ni contratos que terminaron según lo pactado: esos van en sus propias filas.',
  },
  // Una renovación es el mismo cliente con otro código de contrato. Antes se
  // contaba como churn del contrato viejo y como alta del nuevo, e inflaba las
  // dos filas: en septiembre de 2026, 1.797 de las altas eran renovaciones.
  {
    key: 'renewal',
    field: 'renewalMrr',
    label: 'Renovaciones',
    hint: 'Contratos que renovaron. Suma lo que dejó de facturar el contrato anterior y lo que factura el nuevo, así que muestra el cambio de precio de la renovación: si renovó al mismo precio, da cero.',
  },
  {
    key: 'plannedEnd',
    field: 'plannedEndMrr',
    label: 'Fin de contrato pactado',
    hint: 'MRR de contratos que terminaron en la fecha acordada, como pilotos o contratos a plazo fijo. Es MRR que se va, pero no es un cliente perdido.',
  },
];

// B2C se vende directo al consumidor y no cuelga de ningún contrato, así que
// entra al total pero el bridge no puede decir si viene de clientes nuevos o
// existentes. Se muestra aparte en vez de inventarle una categoría.
const UNATTRIBUTED = {
  key: 'unattributed',
  field: 'unattributedMrr',
  label: 'B2C / B2B2C',
  hint: 'B2C y B2B2C Pago Directo: venta directa al consumidor y convenios donde paga el colaborador. No se factura a la empresa, así que su MRR solo se calcula en vivo. Para cada mes cerrado se toma lo que tenía en el último snapshot del mes, y esta fila muestra la diferencia contra el cierre anterior. Antes del 19 de junio de 2026 no hay snapshots.',
};

const CURRENCIES = ['CLP', 'PEN', 'USD', 'MXN'];

// Si el contrato nunca facturó no hay moneda de la cual partir: la del país es
// la que se carga casi siempre.
const CURRENCY_BY_COUNTRY = { Chile: 'CLP', 'Perú': 'PEN', Peru: 'PEN', 'México': 'MXN', Mexico: 'MXN' };

const KIND_LABELS = {
  new: 'Altas', reactivation: 'Reactivaciones', expansion: 'Expansión',
  contraction: 'Contracción', churn: 'Churn', gap: 'Sin datos de facturación',
  renewal: 'Renovaciones', plannedEnd: 'Fin de contrato pactado',
  dataStart: 'Facturación histórica sin cargar',
  noBillingHistory: 'Línea sin facturación mensual (Pago Directo)',
  unattributed: 'B2C / B2B2C',
  missingData: 'Contratos activos sin MRR cargado',
};

/**
 * Criterio de cada fila que tiene uno propio. Va arriba de la tabla porque el
 * modal se abre para saber de dónde sale el número, y eso es lo que responde.
 */
const KIND_HINTS = {
  missingData:
    'Contratos vigentes desde antes de este mes que no tienen nada cargado para el mes. No incluye pausados, internos ni líneas que se calculan solo en vivo (B2B2C Pago Directo, B2C). Se corrige cargando la facturación.',
};



export default {
  name: 'NewMrrReportTable',
  mixins: [utilsMixin],

  data() {
    return {
      // Los contratos de cada movimiento requieren `mrr-new:view-contracts`.
      canSeeContracts: can('mrr-new:view-contracts'),
      loading: false,
      months: [],
      snapshotDate: null,
      year: new Date().getFullYear(),
      detailDialog: false,
      detailRows: [],
      detailTitle: '',
      detailKind: null,
      detailMonth: null,
      loadingDetail: false,
      loadDialog: false,
      loadFormValid: false,
      loadSaving: false,
      loadError: false,
      loadItem: {},
      // Hubo cargas desde el modal: el reporte se refresca al cerrarlo, no
      // con cada guardado, para no mover la tabla de fondo mientras se carga.
      reportStale: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'green',
    };
  },

  computed: {
    breakdownKinds() {
      return BREAKDOWN;
    },

    unattributedRow() {
      return UNATTRIBUTED;
    },

    yearOptions() {
      const current = new Date().getFullYear();
      return [current - 2, current - 1, current, current + 1];
    },

    hasMissingData() {
      return this.months.some(m => m.missingDataCount > 0);
    },
    detailHint() {
      return KIND_HINTS[this.detailKind] || '';
    },
    currencies() {
      return CURRENCIES;
    },
  },

  watch: {
    detailDialog(open) {
      if (!open && this.reportStale) {
        this.reportStale = false;
        this.fetchData();
      }
    },
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    can,
    monthName(month) {
      return MONTHS[month - 1] || '';
    },

    statusLabel(status) {
      if (status === 'closed') return 'cerrado';
      if (status === 'inProgress') return 'en curso';
      return 'proyectado';
    },

    cellClass(month) {
      return `cell cell--${month.status}`;
    },

    /**
     * Variación del MRR Total contra el mes anterior.
     *
     * Se mide sobre el total y no sobre la variación neta, que fue una
     * decisión explícita: es la lectura literal de la fila de arriba. La
     * contrapartida es que arrastra el efecto de los contratos sin cargar —
     * agosto 2026 marca −25% por once contratos pendientes, no por clientes
     * perdidos —, y por eso la fila lleva su advertencia.
     *
     * Enero no tiene contra qué compararse: el diciembre anterior no viene en
     * la serie. Devuelve null en vez de inventar una base.
     */
    growth(index) {
      if (index === 0) return null;
      const previous = this.months[index - 1]?.totalMrr;
      const current = this.months[index]?.totalMrr;
      if (!previous || current === null || current === undefined) return null;
      return (Number(current) - Number(previous)) / Number(previous);
    },

    growthLabel(index) {
      const value = this.growth(index);
      if (value === null) return '—';
      const pct = (value * 100).toFixed(1).replace('.', ',');
      return `${value > 0 ? '+' : ''}${pct}%`;
    },

    growthClass(index) {
      const value = this.growth(index);
      if (value === null) return '';
      return value < 0 ? 'growth--down' : 'growth--up';
    },

    usd(value) {
      if (value === null || value === undefined) return '—';
      const n = Math.round(Number(value) || 0);
      const sign = n < 0 ? '-' : '';
      return `${sign}USD ${Math.abs(n).toLocaleString('es-CL')}`;
    },

    async openMovements(month, kind) {
      if (!this.canSeeContracts) return;
      this.detailKind = kind;
      this.detailMonth = month;
      this.detailTitle = `${KIND_LABELS[kind]} — ${this.monthName(month.month)} ${month.year}`;
      this.detailRows = [];
      this.detailDialog = true;
      this.loadingDetail = true;
      const res = await NewMrrReportService.getMovements({
        year: month.year,
        month: month.month,
        kind,
      });
      this.detailRows = res.status === 'ok' ? res.data : [];
      this.loadingDetail = false;
    },

    defaultCurrency(row) {
      if (CURRENCIES.includes(row.currency)) return row.currency;
      return CURRENCY_BY_COUNTRY[row.pais] || 'CLP';
    },

    openLoad(row) {
      this.loadItem = {
        row,
        contrato: row.contrato,
        empresa: row.empresa,
        month: this.detailMonth.month,
        year: this.detailMonth.year,
        mrrLocal: null,
        moneda: this.defaultCurrency(row),
        notas: null,
      };
      this.loadError = false;
      this.loadDialog = true;
    },

    async saveLoad() {
      if (!this.$refs.loadForm.validate()) return;
      this.loadSaving = true;
      this.loadError = false;
      const { row } = this.loadItem;
      const res = await BillingService.createIndividual({
        companyId: row.companyId,
        contratoId: row.contratoId,
        priceBilling: this.loadItem.mrrLocal,
        currency: this.loadItem.moneda,
        monthBilling: this.loadItem.month,
        yearBilling: this.loadItem.year,
        notas: this.loadItem.notas || undefined,
      });
      this.loadSaving = false;
      if (res.status !== 'ok') {
        this.loadError = true;
        return;
      }
      this.detailRows = this.detailRows.filter(r => r !== row);
      this.reportStale = true;
      this.loadDialog = false;
      this.showSuccessSnackbar(`MRR de ${row.contrato} cargado`);
    },

    async fetchData() {
      this.loading = true;
      const res = await NewMrrReportService.getReport({ year: this.year });
      if (res.status === 'ok') {
        this.months = res.data.months || [];
        this.snapshotDate = res.data.snapshotDate;
      } else {
        this.months = [];
        this.showErrorSnackbar('Ocurrió un error al cargar el reporte de New MRR');
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
/* El signo se lee antes que el número en una fila de porcentajes. */
.growth--up { color: #2e7d52; }
.growth--down { color: #c0392b; }

.table-scroll { overflow-x: auto; }
.mrr-table {
  border-collapse: collapse;
  width: 100%;
  font-family: Inter, sans-serif;
  font-size: 13px;
  background: #fff;
}
.mrr-table th, .mrr-table td { padding: 8px 12px; white-space: nowrap; }
.row-label {
  text-align: left;
  font-weight: 600;
  color: #1B1D21;
  position: sticky;
  left: 0;
  background: #fff;
  min-width: 190px;
}
.row-label--sub { font-weight: 400; color: #555; padding-left: 24px; }
.gap-label { color: #B26500; }
.month-head {
  text-align: right;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #8593A3;
  border-bottom: 1px solid #E5E8EC;
}
.month-status { font-size: 10px; text-transform: none; letter-spacing: 0; }
.month-head--projected { opacity: 0.55; }
.cell { text-align: right; border-bottom: 1px solid #F2F4F7; }
.cell--projected { opacity: 0.55; font-style: italic; }
.cell--inProgress { background: #FFFBF2; }
.clickable { cursor: pointer; }
.clickable:hover { background: #EEF3FA; }
.gap-cell { color: #B26500; }
.section-row td { font-weight: 700; }
.section-title-row td {
  padding-top: 14px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #104B94;
  font-weight: 700;
}
.net-row td { border-top: 1px solid #E5E8EC; font-weight: 600; }
.spacer-row td { height: 10px; border: none; }
.snapshot-note { font-size: 12px; color: #8593A3; }

.detail-hint {
  color: var(--pw-text-muted, #6b7280);
  font-size: 0.8rem;
  line-height: 1.5;
  margin-bottom: 12px;
}
/* Un monto partido en dos renglones ("USD / 706") se lee como dos datos. */
.v-dialog .text-right {
  white-space: nowrap;
}
/* Con ocho columnas, los 16 px por lado de Vuetify empujaban el aporte fuera
   del modal en pantallas de ~900 px. El !important es para ganarle a su
   selector de tabla, que es más específico que cualquier regla con scope. */
.detail-table th,
.detail-table td {
  padding: 0 10px !important;
}
</style>
